import Vue from "vue";

// The chart of accounts, fetched once and kept for the session.
//
// It lives here rather than on the filter module because filterChipsMixin's
// coverage guard walks every key of `filters` and would report a cached tree as
// an applied-but-unchipped filter. Caching matters: the Company tab re-reads the
// tree on every tab switch, every drill-down and every return from a detail
// page, and a chart of accounts does not change while someone is looking at it.
const initialState = () => ({
  tree: [],
  index: {}, // "level:id" -> node, so the cascade resolves a path in O(1)
  loaded: false,
  loading: false,
  unapproved_count: 0,
});

// Flatten once on arrival. The cascade walks a selected path on every keystroke;
// re-searching the tree each time would be the only expensive thing it does.
function buildIndex(nodes, index = {}) {
  nodes.forEach((node) => {
    index[`${node.level}:${node.id}`] = node;
    if (node.children && node.children.length) buildIndex(node.children, index);
  });
  return index;
}

export default {
  namespaced: true,
  state: initialState(),

  mutations: {
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_TREE(state, tree) {
      state.tree = tree;
      state.index = buildIndex(tree);
      state.loaded = true;
      state.loading = false;
    },
    SET_UNAPPROVED_COUNT(state, count) {
      state.unapproved_count = count;
    },
    BUMP_UNAPPROVED_COUNT(state, delta) {
      state.unapproved_count = Math.max(0, state.unapproved_count + delta);
    },
    RESET(state) {
      Object.assign(state, initialState());
    },
  },

  actions: {
    // Returns a promise that resolves once the tree is in hand. Concurrent
    // callers share one request: the three tabs all mount at once and would
    // otherwise each fire their own.
    fetchChart({ state, commit }, { force = false } = {}) {
      if (state.loaded && !force) return Promise.resolve(state.tree);
      if (state.loading && state._inflight) return state._inflight;

      commit("SET_LOADING", true);
      const request = new Promise((resolve) => {
        Vue.prototype.$Axios("get", Vue.prototype.$apiUrl.GET_LEDGER_CHART, {
          // The company tabs would otherwise count every borrower in the book
          // into "Personal Loan and Advance".
          params: { include_borrower: false },
          onSuccess: (res) => {
            commit("SET_TREE", res.data.result.data || []);
            resolve(state.tree);
          },
          onFailure: () => {
            commit("SET_LOADING", false);
            resolve([]);
          },
          isTokenRequired: true,
        });
      });
      // Not reactive state -- just a handle so concurrent callers can share it.
      state._inflight = request;
      return request;
    },

    fetchUnapprovedCount({ commit }) {
      return Vue.prototype.$Axios(
        "get",
        Vue.prototype.$apiUrl.GET_UNAPPROVED_VOUCHER_COUNT,
        {
          onSuccess: (res) => {
            commit("SET_UNAPPROVED_COUNT", res.data.result.data.count || 0);
          },
          onFailure: () => {},
          isTokenRequired: true,
        }
      );
    },

    // Optimistic nudge after an approve or reject, so the badge answers
    // immediately; the next list response replaces it with the real number.
    bumpUnapprovedCount({ commit }, delta) {
      commit("BUMP_UNAPPROVED_COUNT", delta);
    },
  },

  getters: {
    getTree: (state) => state.tree,
    getIndex: (state) => state.index,
    isLoaded: (state) => state.loaded,
    isLoading: (state) => state.loading,
    getUnapprovedCount: (state) => state.unapproved_count,
  },
};
